import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { getRegions } from '../services';
import { useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';

type RegionOption = { label: string; value: string };

const buildRegionOptions = (regions: string[]): RegionOption[] =>
  regions.map(region => ({
    label: region,
    value: region,
  }));

export const useRegions = () => {
  const filtersBody = useVisaoGeralElasticidadeStore(state => state.filtersBody);
  const updateSharedFilters = useVisaoGeralElasticidadeStore.getState().updateSharedFilters;

  const [regionsList, setRegionsList] = useState<string[]>([]);
  const filtersBodyRef = useRef(filtersBody);
  filtersBodyRef.current = filtersBody;

  const mutation = useMutation({
    mutationFn: (filters: typeof filtersBody) => getRegions(filters) as Promise<string[]>,
    onSuccess: response => {
      setRegionsList(response);
    },
  });

  const { mutate } = mutation;

  const fetchRegions = useCallback(() => {
    mutate(filtersBodyRef.current);
  }, [mutate]);

  const regionOptions = useMemo(() => buildRegionOptions(regionsList), [regionsList]);

  const handleOpen = useCallback(() => {
    fetchRegions();
  }, [fetchRegions]);

  const handleRegionSelect = useCallback(
    (value: string) => {
      updateSharedFilters({ region: value ?? '' });
    },
    [updateSharedFilters],
  );

  const handleRegionClear = useCallback(() => {
    updateSharedFilters({ region: '' });
  }, [updateSharedFilters]);

  const handleClearFilters = useCallback(() => {
    handleRegionClear();
  }, [handleRegionClear]);

  const fetchRegionsRef = useRef(fetchRegions);
  fetchRegionsRef.current = fetchRegions;

  useEffect(() => {
    fetchRegionsRef.current();
  }, []);

  return {
    region: filtersBody.region || null,
    regionOptions,
    isLoading: mutation.isPending,
    filterHandlers: {
      handleOpen,
      handleRegionSelect,
      handleRegionClear,
      handleClearFilters,
    },
    error: mutation.error,
    refetch: fetchRegions,
  };
};
